import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircle, AlertCircle, AlertTriangle, Loader,
  ChevronDown, ChevronRight, ChevronLeft, FileCheck, Shield,
  RefreshCw, Download, X
} from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
import {
  runGuidelineReview,
  type LoanFindingRaw,
} from '../../services/guidelinesApiService';
import {
  WorkflowJob,
  JobType,
  StepStatus,
  JobStatus,
  GuidelineRecord,
  Loan,
  LoanFinding,
  FindingStatus,
  GuidelineReviewResults,
} from '../../graphql/types';
import LoanDataSummary, { PurchaseAgreementData } from '../LoanDataSummary';
import type { PropertyAddress } from '../LoanDataSummary';
import { graphqlClient } from '../../services/graphqlClient';

const client = generateClient();

const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' ||
  !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const POLL_INTERVAL_MS = 3000;

interface InvestorGuidelineReviewStepProps {
  job: WorkflowJob;
  jobType: JobType;
  onStepComplete: () => void;
  onBack?: () => void;
}

const InvestorGuidelineReviewStep: React.FC<InvestorGuidelineReviewStepProps> = ({
  job,
  jobType,
  onStepComplete,
  onBack,
}) => {

  // Guideline state
  const [guidelines, setGuidelines] = useState<GuidelineRecord[]>([]);
  const [selectedGuidelineId, setSelectedGuidelineId] = useState<string>('');
  const [loadingGuidelines, setLoadingGuidelines] = useState(true);

  // Review state
  const [findings, setFindings] = useState<LoanFinding[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [expandedLoans, setExpandedLoans] = useState<Set<string>>(new Set());
  const [loadingCachedResults, setLoadingCachedResults] = useState(false);

  // Job state
  const [currentJob, setCurrentJob] = useState<WorkflowJob>(job);
  const [error, setError] = useState<string | null>(null);
  const [completingJob, setCompletingJob] = useState(false);

  // Loan data state (BA jobs only)
  const [loanData, setLoanData] = useState<Loan | null>(null);
  const [loadingLoanData, setLoadingLoanData] = useState(false);
  const [purchaseAgreementData, setPurchaseAgreementData] = useState<PurchaseAgreementData | null>(null);

  // Export state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Polling ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isJobCompleted = currentJob.status === JobStatus.COMPLETED;
  const hasFindings = findings.length > 0;

  // Derived counts for summary bar (Requirement 4.5)
  const passCount = findings.filter(f => f.status === 'PASS').length;
  const failCount = findings.filter(f => f.status === 'FAIL').length;
  const warningCount = findings.filter(f => f.status === 'WARNING').length;
  const failedCount = findings.filter(f => f.status === 'FAILED').length;
  const missingDataCount = findings.filter(f => f.status === 'MISSING DATA').length;

  // ─── Task 10.1: Load guidelines on mount ───
  const loadGuidelines = useCallback(async () => {
    setLoadingGuidelines(true);
    setError(null);
    try {
      if (isPreviewMode) {
        const mock: GuidelineRecord[] = [
          { tenantId: 'platform', guidelineId: 'gl-1', name: 'Fannie Mae Selling Guide', description: 'Standard Fannie Mae eligibility and documentation requirements' },
          { tenantId: 'platform', guidelineId: 'gl-2', name: 'Freddie Mac Guide', description: 'Freddie Mac loan purchase eligibility criteria' },
          { tenantId: 'tenant-1', guidelineId: 'gl-3', name: 'Custom Investor A', description: 'Custom criteria for Investor A portfolio' },
        ];
        setGuidelines(mock);
        setLoadingGuidelines(false);
        return;
      }

      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      const result = await client.graphql({
        query: queries.listGuidelines,
        variables: { tenantId },
      }) as any;
      const list: GuidelineRecord[] = result.data?.listGuidelines || [];
      setGuidelines(list);
    } catch (err) {
      console.error('Failed to load guidelines:', err);
      setError('Failed to load investor guidelines');
    } finally {
      setLoadingGuidelines(false);
    }
  }, []);

  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);
  useEffect(() => { setCurrentJob(job); }, [job]);

  // ─── Fetch loan data for BA jobs ───
  useEffect(() => {
    const fetchLoanData = async () => {
      if (jobType !== JobType.BA || isPreviewMode) return;
      setLoadingLoanData(true);
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        // BA jobs have exactly one loan — fetch the list to get the loan ID
        const loansResult = await graphqlClient.queries.listLoansByJob(job.jobId, tenantId);
        if (loansResult.items && loansResult.items.length > 0) {
          const loanId = loansResult.items[0].loanId;
          // Fetch full loan data including coBorrowerNames
          const loan = await graphqlClient.queries.getLoan(loanId, job.jobId, tenantId);
          setLoanData(loan);

          // Fetch documents to extract purchase agreement data
          try {
            const docsResult = await graphqlClient.queries.listDocumentsByLoan(loanId, job.jobId, tenantId);
            const purchaseDoc = docsResult.items?.find(
              (doc) => doc.documentType === 'Real Estate Purchase Agreement'
            );
            if (purchaseDoc?.extractedFields) {
              const fields: { field_name: string; field_value: string }[] =
                typeof purchaseDoc.extractedFields === 'string'
                  ? JSON.parse(purchaseDoc.extractedFields)
                  : purchaseDoc.extractedFields;

              // Build a case-insensitive field map
              const fieldMap: Record<string, string> = {};
              if (Array.isArray(fields)) {
                fields.forEach((f) => { fieldMap[f.field_name?.toLowerCase() ?? ''] = f.field_value; });
              } else if (typeof fields === 'object' && fields !== null) {
                Object.entries(fields).forEach(([k, v]) => { fieldMap[k.toLowerCase()] = String(v); });
              }

              // Parse Property Address — BDA returns it as a dict-like string or object
              const rawAddress = fieldMap['property address'] || fieldMap['property_address'] || '';
              let propertyAddress: PropertyAddress = {
                streetAddress: '', county: '', city: '', state: '', zipcode: '',
              };
              if (rawAddress) {
                try {
                  // BDA may return a Python-style dict string: {'Street Address': '...', 'County': '...'}
                  const normalized = rawAddress.replace(/'/g, '"');
                  const parsed = JSON.parse(normalized);
                  if (typeof parsed === 'object' && parsed !== null) {
                    // Case-insensitive key lookup
                    const addrMap: Record<string, string> = {};
                    Object.entries(parsed).forEach(([k, v]) => { addrMap[k.toLowerCase()] = String(v); });
                    propertyAddress = {
                      streetAddress: addrMap['street address'] || addrMap['street_address'] || addrMap['address'] || '',
                      county: addrMap['county'] || '',
                      city: addrMap['city'] || '',
                      state: addrMap['state'] || '',
                      zipcode: addrMap['zipcode'] || addrMap['zip code'] || addrMap['zip'] || addrMap['postal code'] || '',
                    };
                  }
                } catch {
                  // If parsing fails, treat the whole string as street address
                  propertyAddress.streetAddress = rawAddress;
                }
              }

              // Extract buyer and seller names (BDA uses "Buyer's Name" / "Seller's Name")
              const buyerName =
                fieldMap["buyer's name"] || fieldMap['buyer name'] || fieldMap['buyername'] ||
                fieldMap['buyer_name'] || fieldMap['buyer'] || '';
              const sellerName =
                fieldMap["seller's name"] || fieldMap['seller name'] || fieldMap['sellername'] ||
                fieldMap['seller_name'] || fieldMap['seller'] || '';

              // Extract financial fields
              const purchasePrice = fieldMap['purchase price'] || fieldMap['purchase_price'] || '';
              const earnestMoneyAmount = fieldMap['earnest money amount'] || fieldMap['earnest_money_amount'] || '';
              const closingDate = fieldMap['closing date'] || fieldMap['closing_date'] || '';

              // Extract deadlines from fields containing "deadline" or "contingency"
              const deadlines: { name: string; date: string }[] = [];
              const deadlineKeywords = ['deadline', 'contingency'];
              Object.entries(fieldMap).forEach(([key, value]) => {
                if (deadlineKeywords.some((kw) => key.includes(kw)) && value) {
                  deadlines.push({
                    name: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                    date: value,
                  });
                }
              });

              // Also include Possession Delivery Date if present
              const possessionDate = fieldMap['possession delivery date'] || fieldMap['possession_delivery_date'] || '';
              if (possessionDate) {
                deadlines.push({ name: 'Possession Delivery Date', date: possessionDate });
              }

              setPurchaseAgreementData({
                propertyAddress, buyerName, sellerName,
                purchasePrice, earnestMoneyAmount, closingDate, deadlines,
              });
            }
          } catch (docErr) {
            console.error('Failed to load purchase agreement data:', docErr);
          }
        }
      } catch (err) {
        console.error('Failed to load loan data for BA job:', err);
      } finally {
        setLoadingLoanData(false);
      }
    };
    fetchLoanData();
  }, [job.jobId, jobType]);
  // Note: do NOT call onStepComplete() reactively when isJobCompleted flips to true.
  // handleCompleteJob already invokes it once. A reactive effect here caused an
  // infinite loop because onStepComplete triggers loadJobData in the parent, which
  // toggles the parent's `loading` flag and unmounts/remounts this component —
  // remount would re-fire the effect, re-triggering loadJobData, etc.

  // ─── Auto-select last used guideline for this job ───
  useEffect(() => {
    if (guidelines.length === 0 || !job.jobId) return;
    
    // Try to load the last used guideline for this job from localStorage
    const lastGuidelineKey = `lastGuideline_${job.jobId}`;
    const lastGuidelineId = localStorage.getItem(lastGuidelineKey);
    
    if (lastGuidelineId && guidelines.some(g => g.guidelineId === lastGuidelineId)) {
      setSelectedGuidelineId(lastGuidelineId);
      console.log(`Auto-selected last used guideline: ${lastGuidelineId}`);
    }
  }, [guidelines, job.jobId]);

  // ─── Save selected guideline to localStorage ───
  useEffect(() => {
    if (selectedGuidelineId && job.jobId) {
      localStorage.setItem(`lastGuideline_${job.jobId}`, selectedGuidelineId);
    }
  }, [selectedGuidelineId, job.jobId]);

  // ─── Load cached results when guideline is selected ───
  useEffect(() => {
    const loadCachedResults = async () => {
      console.log('🔍 [GuidelineReview] loadCachedResults triggered', {
        selectedGuidelineId,
        jobId: job.jobId,
        isPreviewMode
      });
      
      if (!selectedGuidelineId || !job.jobId || isPreviewMode) {
        console.log('⏭️ [GuidelineReview] Skipping cache load:', {
          hasGuideline: !!selectedGuidelineId,
          hasJobId: !!job.jobId,
          isPreview: isPreviewMode
        });
        return;
      }

      const canUseCachedResults =
        currentJob.investorGuidelineReviewStatus === StepStatus.COMPLETED ||
        currentJob.status === JobStatus.COMPLETED;
      if (!canUseCachedResults) {
        setFindings([]);
        return;
      }
      
      // Clear previous findings immediately so stale data doesn't linger
      setFindings([]);
      setLoadingCachedResults(true);
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        
        console.log('📡 [GuidelineReview] Calling getGuidelineReviewResults', {
          jobId: job.jobId,
          guidelineId: selectedGuidelineId,
          tenantId
        });
        
        const result = await client.graphql({
          query: queries.getGuidelineReviewResults,
          variables: { jobId: job.jobId, tenantId, guidelineId: selectedGuidelineId },
        }) as any;
        
        const cached = result.data?.getGuidelineReviewResults;
        
        console.log('✅ [GuidelineReview] Received response:', cached);
        
        if (cached?.findings) {
          // Map the cached results to frontend format
          const mapped: LoanFinding[] = cached.findings.map((f: any) => ({
            loanId: f.loanId,
            status: f.status as FindingStatus,
            explanation: f.explanation,
            unmetCriteria: f.unmetCriteria,
          }));
          setFindings(mapped);
          console.log(`✅ [GuidelineReview] Loaded ${mapped.length} cached findings for guideline ${selectedGuidelineId}`);
        } else {
          // Ensure findings are cleared when no cached results exist for this guideline
          setFindings([]);
          console.log('⚠️ [GuidelineReview] No cached guideline review results found');
        }
      } catch (err) {
        console.error('❌ [GuidelineReview] Failed to load cached results:', err);
        // Not a critical error - user can still run review
      } finally {
        setLoadingCachedResults(false);
      }
    };
    
    loadCachedResults();
  }, [selectedGuidelineId, job.jobId, currentJob.investorGuidelineReviewStatus, currentJob.status]);

  // Close export menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ─── Task 10.2: Run review via direct Lambda Function URL call ───
  const handleRunReview = async () => {
    if (!selectedGuidelineId) {
      setError('Please select a guideline');
      return;
    }
    setReviewing(true);
    setError(null);
    setFindings([]);

    try {
      if (isPreviewMode) {
        await new Promise(r => setTimeout(r, 2000));
        const mockFindings: LoanFinding[] = [
          { loanId: 'loan-001', status: 'PASS', explanation: 'All criteria met. Income documentation verified, DTI within limits.' },
          { loanId: 'loan-002', status: 'FAIL', explanation: 'Borrower DTI ratio exceeds 43% limit.', unmetCriteria: ['DTI ratio must not exceed 43%', 'Requires additional income documentation'] },
          { loanId: 'loan-003', status: 'WARNING', explanation: 'Property appraisal is dated 115 days ago, approaching 120-day limit.', unmetCriteria: ['Appraisal date within 120 days'] },
          { loanId: 'loan-004', status: 'PASS', explanation: 'All documentation present and within acceptable ranges.' },
          { loanId: 'loan-005', status: 'FAILED', explanation: 'Agent error: Unable to parse extracted fields for this loan.' },
        ];
        setFindings(mockFindings);
        setReviewing(false);
        return;
      }

      const result = await runGuidelineReview({
        guideline_id: selectedGuidelineId,
        job_id: job.jobId,
      });

      // Map snake_case API response to camelCase frontend types
      const mapped: LoanFinding[] = result.findings.map((f: LoanFindingRaw) => ({
        loanId: f.loan_id,
        status: f.status as FindingStatus,
        explanation: f.explanation,
        unmetCriteria: f.unmet_criteria,
      }));
      setFindings(mapped);
    } catch (err: any) {
      console.error('Failed to run review:', err);
      setError(err?.message || 'Failed to run guideline review. Please try again.');
    } finally {
      setReviewing(false);
    }
  };

  // ─── Task 10.5: Complete Job ───
  const handleCompleteJob = async () => {
    setCompletingJob(true);
    setError(null);
    try {
      if (isPreviewMode) {
        setCurrentJob(prev => ({ ...prev, status: JobStatus.COMPLETED }));
        onStepComplete();
        setCompletingJob(false);
        return;
      }

      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      const result = await client.graphql({
        query: mutations.updateJobStatus,
        variables: {
          jobId: job.jobId,
          tenantId,
          status: 'COMPLETED',
          completedAt: new Date().toISOString(),
        },
      }) as any;
      const updated = result.data?.updateJobStatus;
      if (updated) {
        setCurrentJob(prev => ({ ...prev, status: JobStatus.COMPLETED }));
        onStepComplete();
      }
    } catch (err) {
      console.error('Failed to complete job:', err);
      setError('Failed to mark job as completed. Please try again.');
    } finally {
      setCompletingJob(false);
    }
  };

  // ─── Task 10.6: Export findings ───
  const handleExport = (format: 'csv' | 'xlsx') => {
    setShowExportMenu(false);
    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = `findings-${job.jobId}-${dateStr}`;

    if (format === 'csv') {
      exportCsv(findings, `${filename}.csv`);
    } else {
      exportXlsx(findings, `${filename}.xlsx`);
    }
  };

  const toggleLoanExpansion = (loanId: string) => {
    setExpandedLoans(prev => {
      const next = new Set(prev);
      if (next.has(loanId)) next.delete(loanId);
      else next.add(loanId);
      return next;
    });
  };

  const getStatusBadge = (status: FindingStatus) => {
    switch (status) {
      case 'PASS':
        return { className: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Pass' };
      case 'FAIL':
        return { className: 'bg-red-100 text-red-800', icon: X, label: 'Fail' };
      case 'WARNING':
        return { className: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: 'Warning' };
      case 'FAILED':
        return { className: 'bg-gray-100 text-gray-600', icon: AlertCircle, label: 'Failed' };
      case 'MISSING DATA':
        return { className: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'Missing Data' };
    }
  };

  const selectedGuideline = guidelines.find(g => g.guidelineId === selectedGuidelineId);

  // ─── Loading state ───
  if (loadingGuidelines) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <Loader className="h-10 w-10 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Loading investor guidelines...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {jobType === JobType.BA ? 'Underwriting Guideline Review' : 'Investor Guideline Review'}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {!hasFindings && !reviewing && (jobType === JobType.BA
              ? 'Select an underwriting guideline and run the AI-powered review.'
              : 'Select an investor guideline and run the AI-powered review.')}
            {reviewing && 'Review in progress — analyzing loans against the selected guideline...'}
            {hasFindings && !isJobCompleted && 'Review complete. Examine findings below.'}
            {isJobCompleted && 'Job completed. All findings are finalized.'}
          </p>
        </div>
      </div>

      {/* Loan Data Summary for BA jobs (Requirement 5.1, 5.6) */}
      {jobType === JobType.BA && loanData && (
        <LoanDataSummary loan={loanData} purchaseAgreementData={purchaseAgreementData} />
      )}
      {jobType === JobType.BA && loadingLoanData && (
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Loader className="h-4 w-4 animate-spin" />
          <span>Loading loan data...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-white">{error}</p>
          </div>
        </div>
      )}

      {/* Completed Banner (Req 12.6) */}
      {isJobCompleted && (
        <div className="border rounded-xl p-5 bg-green-50 border-green-200">
          <div className="flex items-start space-x-4">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800">Job Completed</p>
              <p className="mt-1 text-xs text-green-700">
                This job has been finalized. No further reviews can be run.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Guideline Selection — Task 10.1 (Req 1.1–1.5) */}
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {isJobCompleted ? 'View Results by Guideline' : hasFindings ? 'Re-run with Different Guideline' : 'Select Investor Guideline'}
          </h3>
        </div>

        {guidelines.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">
            No guidelines configured for your tenant. Please contact your administrator.
          </p>
        ) : (
          <>
            <label htmlFor="guideline-select" className="block text-sm font-medium text-gray-700 mb-1">
              Guideline
            </label>
            <select
              id="guideline-select"
              value={selectedGuidelineId}
              onChange={e => setSelectedGuidelineId(e.target.value)}
              disabled={reviewing}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">— Select a guideline —</option>
              {guidelines.map(g => (
                <option key={g.guidelineId} value={g.guidelineId}>{g.name}</option>
              ))}
            </select>

            {/* Selected guideline description (Req 1.5) */}
            {selectedGuideline && (
              <p className="mt-2 text-sm text-gray-600">
                {selectedGuideline.description}
              </p>
            )}

            {/* Run Review button (Req 1.4, 3.4) — hidden when job is completed */}
            {!isJobCompleted && (
              <div className="mt-4 flex justify-end">
                {loadingCachedResults && (
                  <span className="text-sm text-gray-500 flex items-center mr-4">
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Loading cached results...
                  </span>
                )}
                <button
                  onClick={handleRunReview}
                  disabled={!selectedGuidelineId || reviewing || loadingCachedResults}
                  className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                    !selectedGuidelineId || reviewing || loadingCachedResults
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {reviewing ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Reviewing...
                    </>
                  ) : (
                    <>
                      {hasFindings ? <RefreshCw className="h-4 w-4 mr-2" /> : <FileCheck className="h-4 w-4 mr-2" />}
                      {hasFindings ? 'Re-run Review' : 'Run Review'}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Loading indicator for cached results when job is completed */}
            {isJobCompleted && loadingCachedResults && (
              <div className="mt-4 flex justify-end">
                <span className="text-sm text-gray-500 flex items-center">
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Loading cached results...
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Findings Section — Task 10.3 (Req 4.1–4.6) */}
      {hasFindings && (
        <>
          {/* Summary Count Bar (Req 4.5) — hidden for BA jobs (single loan) */}
          {jobType !== JobType.BA && (
          <div className="grid grid-cols-5 gap-4">
            <SummaryCard label="Pass" count={passCount} color="green" />
            <SummaryCard label="Fail" count={failCount} color="red" />
            <SummaryCard label="Warning" count={warningCount} color="yellow" />
            <SummaryCard label="Missing Data" count={missingDataCount} color="orange" />
            <SummaryCard label="Failed" count={failedCount} color="gray" />
          </div>
          )}

          {/* Loan Findings List */}
          <div className="bg-white shadow-xl rounded-xl border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Loan Findings</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {findings.map(finding => {
                const badge = getStatusBadge(finding.status);
                const StatusIcon = badge.icon;
                const isExpanded = expandedLoans.has(finding.loanId);

                return (
                  <div key={finding.loanId}>
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleLoanExpansion(finding.loanId)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleLoanExpansion(finding.loanId); }}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                        <StatusIcon className={`h-5 w-5 ${
                          finding.status === 'PASS' ? 'text-green-500' :
                          finding.status === 'FAIL' ? 'text-red-500' :
                          finding.status === 'WARNING' ? 'text-yellow-500' :
                          finding.status === 'MISSING DATA' ? 'text-orange-500' : 'text-gray-400'
                        }`} />
                        <span className="font-medium text-gray-900">Loan {finding.loanId}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.className}`}>{badge.label}</span>
                    </div>

                    {/* Expanded detail (Req 4.3, 4.4) */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pl-12">
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="text-sm text-gray-700">
                            {formatExplanation(finding.explanation)}
                          </div>
                          {finding.unmetCriteria && finding.unmetCriteria.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Unmet Criteria:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {finding.unmetCriteria.map((c, i) => (
                                  <li key={i} className="text-sm text-red-700">{c}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons: Back + Complete Job + Export — Tasks 10.5, 10.6 */}
          <div className="flex justify-between space-x-3">
            {/* Back button */}
            {onBack && (
              <button
                onClick={onBack}
                className="inline-flex items-center px-4 py-2.5 rounded-lg font-medium text-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back: Document Verification
              </button>
            )}
            <div className="flex space-x-3 ml-auto">
            {/* Export Button (Req 13.1–13.7) */}
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(prev => !prev)}
                disabled={!hasFindings}
                className={`inline-flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  hasFindings
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                  >
                    Download as CSV
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg"
                  >
                    Download as Excel (.xlsx)
                  </button>
                </div>
              )}
            </div>

            {/* Complete Job Button (Req 12.1–12.7) */}
            <button
              onClick={handleCompleteJob}
              disabled={!hasFindings || isJobCompleted || completingJob}
              className={`inline-flex items-center px-6 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                hasFindings && !isJobCompleted && !completingJob
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {completingJob ? (
                <><Loader className="h-4 w-4 mr-2 animate-spin" />Completing...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" />Complete Job</>
              )}
            </button>
          </div>
            </div>
        </>
      )}

      {/* Reviewing indicator */}
      {reviewing && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className="h-10 w-10 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-600">Running AI-powered guideline review...</p>
          <p className="text-xs text-gray-500 mt-1">This may take a few minutes depending on the number of loans.</p>
        </div>
      )}
    </div>
  );
};

// ─── Summary Card sub-component ───
const SummaryCard: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => {
  const colorMap: Record<string, string> = {
    green: 'bg-green-50 border-green-200 text-green-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-600',
  };
  return (
    <div className={`rounded-xl border p-4 text-center ${colorMap[color] || colorMap.gray}`}>
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs font-medium mt-1">{label}</p>
    </div>
  );
};

// ─── Helper function to format explanation with bullet points ───
function formatExplanation(explanation: string): React.ReactNode {
  // Split by newlines and detect bullet points (-, *, •, or numbered lists)
  const lines = explanation.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  // Check if the explanation contains bullet points
  const hasBullets = lines.some(line => 
    line.startsWith('-') || 
    line.startsWith('*') || 
    line.startsWith('•') ||
    /^\d+\./.test(line)
  );

  if (!hasBullets) {
    // No bullet points detected, render as plain text
    return <p>{explanation}</p>;
  }

  // Render as a list
  const items: React.ReactNode[] = [];
  let currentText: string[] = [];

  lines.forEach((line, index) => {
    const isBullet = line.startsWith('-') || line.startsWith('*') || line.startsWith('•') || /^\d+\./.test(line);
    
    if (isBullet) {
      // Add any accumulated non-bullet text as a paragraph
      if (currentText.length > 0) {
        items.push(<p key={`text-${index}`}>{currentText.join(' ')}</p>);
        currentText = [];
      }
      
      // Remove bullet marker and add as list item
      const text = line.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '');
      items.push(<li key={`bullet-${index}`} className="ml-4">{text}</li>);
    } else {
      // Accumulate non-bullet text
      currentText.push(line);
    }
  });

  // Add any remaining text
  if (currentText.length > 0) {
    items.push(<p key="text-final">{currentText.join(' ')}</p>);
  }

  return <ul className="list-disc space-y-1">{items}</ul>;
}

// ─── Export utilities (Task 10.6) ───

/** Generate and download CSV from findings (Req 13.4, 13.6, 13.7) */
export function exportCsv(findings: LoanFinding[], filename: string): void {
  const header = 'Loan ID,Status,Explanation,Unmet Criteria';
  const rows = findings.map(f => {
    const escapeCsv = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const criteria = f.unmetCriteria ? f.unmetCriteria.join('; ') : '';
    return `${escapeCsv(f.loanId)},${f.status},${escapeCsv(f.explanation)},${escapeCsv(criteria)}`;
  });
  const csv = [header, ...rows].join('\n');
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

/** Generate and download Excel from findings using xlsx library (Req 13.5, 13.6, 13.7) */
export async function exportXlsx(findings: LoanFinding[], filename: string): Promise<void> {
  try {
    const XLSX = await import('xlsx');
    const data = findings.map(f => ({
      'Loan ID': f.loanId,
      'Status': f.status,
      'Explanation': f.explanation,
      'Unmet Criteria': f.unmetCriteria ? f.unmetCriteria.join('; ') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Findings');
    XLSX.writeFile(wb, filename);
  } catch {
    // Fallback: if xlsx not available, export as CSV
    console.warn('xlsx library not available, falling back to CSV export');
    exportCsv(findings, filename.replace('.xlsx', '.csv'));
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default InvestorGuidelineReviewStep;
